import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Settings, LogOut, Shield, ChevronDown, LifeBuoy, ShieldCheck, Flag, CreditCard } from 'lucide-react';

/**
 * Floating top-right user menu for authenticated users.
 * Rendered globally from `App.js` and self-hides on public/auth/landing pages
 * (`/`, `/login`, `/register`, public leaderboards, /join, /team/:token, etc.)
 * via the `excludePaths` array below.
 */
const EXCLUDE_PATH_PREFIXES = [
  '/',                  // landing — already has its own nav user pill
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/share/',            // public shared schedule
  '/stats/leaderboard/',// public leaderboard
  '/team/',             // public team roster page
  '/join',              // public guest stat-keeper join
  '/blog',
  '/terms',
  '/privacy',
  '/pricing',
  '/events/',           // print-friendly stat sheets
];

function shouldHide(pathname) {
  // Exact match for landing page (deeper paths starting with '/' should NOT be hidden)
  if (pathname === '/') return true;
  // For every other prefix, strip trailing slash so prefixes like '/share/'
  // correctly match '/share/{token}' via startsWith(norm + '/').
  return EXCLUDE_PATH_PREFIXES.slice(1).some(p => {
    const norm = p.replace(/\/$/, '');
    return pathname === norm || pathname.startsWith(norm + '/');
  });
}

export default function UserMenu({ inline = false }) {
  const { user, isAuthenticated, isAdmin, isPro, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Hide while we don't yet have a user, on public/landing pages, etc.
  if (!isAuthenticated || !user) return null;
  // shouldHide only applies to the floating variant — inline is opted-in
  // by the host page and always shown.
  if (!inline && shouldHide(location.pathname)) return null;

  const initial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  // Wrapper positioning: fixed for the floating global menu, relative for
  // the inline variant so it flows naturally inside a page header.
  const wrapperClass = inline
    ? "relative z-40 print:hidden"
    : "sg-floating-user-menu fixed top-3 right-3 sm:top-4 sm:right-4 z-[100] print:hidden";

  return (
    <div ref={ref} className={wrapperClass} data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-md hover:shadow-lg hover:border-[#00247D]/30 transition-all"
        data-testid="user-menu-trigger"
        title="Account"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#00247D] text-white flex items-center justify-center text-xs font-semibold">
            {initial}
          </div>
        )}
        <span className="hidden sm:inline text-sm font-medium text-slate-700 max-w-[100px] truncate">
          {user.name?.split(' ')[0] || 'You'}
        </span>
        {isPro && (
          <span className="hidden sm:inline px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded uppercase tracking-wide">PRO</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-xl bg-white border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-800 truncate">{user.name || 'User'}</div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
            {user.organization && (
              <div className="text-[11px] text-slate-400 mt-0.5 truncate">{user.organization}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/settings'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            data-testid="user-menu-settings-btn"
          >
            <Settings className="w-4 h-4 text-slate-500" />
            <span>Settings</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/billing'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            data-testid="user-menu-billing-btn"
          >
            <CreditCard className="w-4 h-4 text-slate-500" />
            <span>Billing</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/officials'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            data-testid="user-menu-officials-btn"
          >
            <ShieldCheck className="w-4 h-4 text-slate-500" />
            <span>Officials Hub</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/officials/portal'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            data-testid="user-menu-officials-portal-btn"
          >
            <Flag className="w-4 h-4 text-slate-500" />
            <span>My Officiating</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/support'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            data-testid="user-menu-support-btn"
          >
            <LifeBuoy className="w-4 h-4 text-slate-500" />
            <span>Support</span>
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/admin'); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
              data-testid="user-menu-admin-btn"
            >
              <Shield className="w-4 h-4 text-slate-500" />
              <span>Admin Dashboard</span>
            </button>
          )}
          <div className="border-t border-slate-100">
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              data-testid="user-menu-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
